import * as React from "react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { ExternalLink } from "lucide-react";
import { urlValue } from "@/lib/stores/objectsStore";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface HyperLinkElementProps {
  type: string;
  value: urlValue;
  onChange: (value: urlValue) => void;
  onBlur?: (value: urlValue) => void;
  placeholder?: string;
  /** If true, the content is not editable and shows as a clickable link */
  disabled?: boolean;
  /**
   * Rendering mode when not editing:
   *  - "view-formatted": render as clickable hyperlink
   *  - "view-raw": render the literal URL text
   * Default is "view-formatted".
   */
  viewMode?: "view-formatted" | "view-raw";
}

const HyperLinkElement = React.forwardRef<
  HTMLInputElement,
  HyperLinkElementProps
>(
  (
    {
      type,
      value,
      onChange,
      onBlur,
      placeholder = "Enter URL...",
      disabled = false,
      viewMode = "view-formatted",
      ...props
    },
    ref
  ) => {
    // Initialize local state from urlValue object
    const [url, setUrl] = useState(value.url || "");
    const [text, setText] = useState(value.text || "");
    const { t } = usePlatformTranslation();


    // Update local state when value prop changes
    useEffect(() => {
      setUrl(value.url || "");
      setText(value.text || "");
    }, [value]);

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newUrl = e.target.value;
      setUrl(newUrl);
      // Always call onChange to ensure changes are saved
      const urlValueObj: urlValue = {
        url: newUrl,
        text: text,
      };
      onChange(urlValueObj);
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newText = e.target.value;
      setText(newText);
      // Always call onChange to ensure text changes are saved
      const urlValueObj: urlValue = {
        url: url,
        text: newText,
      };
      onChange(urlValueObj);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        const urlValueObj: urlValue = {
          url: url,
          text: text,
        };
        onBlur(urlValueObj);
      }
    };

    // Non-edit mode: show as clickable link or raw text
    if (disabled) {
      const baseClasses = cn(
        "flex w-full border border-input bg-background px-3 py-2 text-base ring-offset-background md:text-sm",
        "overflow-visible"
      );

      if (viewMode === "view-formatted" && url) {
        return (
          <div className={baseClasses}>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 underline cursor-pointer"
            >
              {text || url}
            </a>
          </div>
        );
      }

      // Default: show raw content
      return (
        <div className={baseClasses}>
          <span className="text-muted-foreground">
            {url || "No URL provided"}
          </span>
        </div>
      );
    }

    // Edit mode: show URL and text inputs
    return (
      <div className="space-y-2">
        <input
          type="url"
          className={cn(
            "flex h-10 w-full border border-input bg-background text-foreground px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
          )}
          placeholder={t('platform.wizards.wizard-elements.enter_url')}
          value={url}
          onChange={handleUrlChange}
          onBlur={handleBlur}
          ref={ref}
          {...props}
        />
        <input
          type="text"
          className={cn(
            "flex h-10 w-full border border-input bg-background text-foreground px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
          )}
          placeholder={t('platform.wizards.wizard-elements.enter_link_text_optional')}
          value={text}
          onChange={handleTextChange}
        />
      </div>
    );
  }
);
HyperLinkElement.displayName = "HyperLinkElement";

export { HyperLinkElement };
